import classNames from "classnames"
import type { ReactNode, MouseEvent, ForwardedRef } from "react"
import { forwardRef } from "react"
import { darkenHexColor } from "~/helpers/colors"

export interface ButtonProps {
  children: React.ReactNode
  variant?: "primary" | "secondary" | "action" | "white" | "danger"
  size?: "small" | "medium" | "large" | "xlarge" | "responsive"
  style?: "normal" | "inverted" | "outlined" | "dropShadow"
  textAlign?: "left" | "center" | "right"
  removePadding?: boolean
  full?: boolean | { sm?: boolean; md?: boolean; lg?: boolean }
  disabled?: boolean
  customBackgroundColor?: string | undefined | null
  customTextColor?: string | undefined | null
  icon?: ReactNode
  iconOnly?: boolean
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  type?: "button" | "submit"
  ariaLabel?: string
  ariaLabelledBy?: string
  pulse?: boolean
  customBorderColor?: string
  className?: string
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "medium",
      style = "normal",
      textAlign = "center",
      removePadding,
      full,
      disabled,
      customBackgroundColor,
      customTextColor,
      icon,
      iconOnly,
      children,
      onClick,
      type = "button",
      ariaLabel,
      ariaLabelledBy,
      pulse,
      customBorderColor,
      className
    },
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const buttonClassName = classNames(
      "flex items-center enabled:cursor-pointer disabled:opacity-25 transition-transform duration-100 ease-[linear(0, 0.618 4.6%, 1.072 9.7%, 1.358 15.3%, 1.446 18.4%, 1.497 21.7%, 1.512 23.9%, 1.514 26.2%, 1.481 31.5%, 1.421 36.4%, 1.174 53.4%, 1.108 59.5%, 1.059 65.7%, 1.028 71.9%, 1.009 78.9%, 1)]",
      variant === "secondary" ? "rounded-lg" : "rounded-full",
      {
        "w-full": full === true,
        "w-full md:w-auto": typeof full === "object" && full.sm,
        "w-full lg:w-auto": typeof full === "object" && full.md,
        "w-full xl:w-auto": typeof full === "object" && full.lg,
        "active:scale-[99%]": size === "xlarge",
        "active:scale-[97%]": size !== "xlarge",
        "focus:ring-blue-dark-500": !customBackgroundColor
      },
      className
    )

    const styleNormalClasses = {
      "bg-camp-primary text-white": variant == "primary",
      "bg-blue-dark-500 text-white": variant == "action",
      "bg-white text-blue-dark-500 border border-blue-dark-500":
        variant == "secondary",
      "bg-red-500 text-white": variant == "danger",
      "bg-white text-gray-300": variant == "white"
    }

    const styleOutlinedClasses = {
      "border-camp-primary text-camp-primary": variant == "primary",
      "border-text-blue-dark-500 text-blue-dark-500": variant == "action",
      "border-text-teal-500 text-teal-500": variant == "secondary",
      "border-white text-white": variant == "white",
      "border-red-500 text-red-500": variant == "danger"
    }

    const variantClasses =
      style == "outlined" || style == "inverted"
        ? styleOutlinedClasses
        : styleNormalClasses

    const sizeClasses = {
      "h-8 text-xs": size == "small",
      "h-10 text-sm": size == "medium",
      "h-12 text-base font-medium": size == "large",
      "h-14 text-base font-medium": size == "xlarge",
      "h-10 text-sm font-base lg:h-12 lg:text-base lg:font-medium":
        size == "responsive"
    }

    const buttonStyle =
      style == "outlined" || style == "inverted"
        ? {
            borderColor: customBackgroundColor
              ? darkenHexColor(customBackgroundColor, 20)
              : undefined,
            color: customTextColor ?? undefined
          }
        : style == "dropShadow"
          ? {
              borderColor: customBorderColor ?? undefined,
              borderBottom: `2px solid ${customBorderColor ?? "undefined"}`,
              borderRight: `2px solid ${customBorderColor ?? "undefined"}`,
              backgroundColor: customBackgroundColor ?? undefined,
              boxShadow: `0.25rem 0.25rem ${customBackgroundColor}`,
              color: customTextColor ?? undefined
            }
          : {
              backgroundColor: customBackgroundColor ?? undefined,
              color: customTextColor ?? undefined
            }

    const iconOnlySizeClasses = {
      "w-8 h-8": size == "small",
      "w-10 h-10": size == "medium",
      "w-12 h-12": size == "large" || size == "xlarge",
      "w-10 h-10 lg:w-12 lg:h-12": size == "responsive"
    }

    const content = (
      <div
        className={classNames(
          "flex items-center whitespace-nowrap",
          variant === "secondary" ? "rounded-lg" : "rounded-full",
          variantClasses,
          iconOnly ? iconOnlySizeClasses : sizeClasses,
          iconOnly
            ? { "justify-center": true }
            : {
                "w-full": true,
                "px-3": size == "small" && !removePadding,
                "px-3 md:px-5": size == "medium" && !removePadding,
                "px-7": (size == "large" || size == "xlarge") && !removePadding,
                "px-3 md:px-5 lg:px-7": size == "responsive" && !removePadding,
                "justify-center": textAlign == "center" && !icon,
                "justify-start": textAlign == "left" && !icon,
                "justify-end": textAlign == "right" && !icon,
                "justify-between": !!icon
              },
          {
            "motion-safe:animate-colorPulse": pulse,
            "border-[1.5px]": style == "outlined",
            "bg-white": style == "inverted" || style == "outlined"
          }
        )}
        style={buttonStyle}
      >
        {pulse ? (
          <div
            className="absolute w-[55%] rounded-full h-2/3 animate-ping m-auto z-0"
            style={buttonStyle}
          />
        ) : null}
        {iconOnly ? icon : children}
        {!iconOnly && icon && <span className="ml-4">{icon}</span>}
      </div>
    )

    return (
      <button
        type={type}
        className={buttonClassName}
        disabled={disabled}
        onClick={onClick}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        ref={ref}
      >
        {content}
      </button>
    )
  }
)

Button.displayName = "Button"

export { Button }
export default Button
